import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useRef, useImperativeHandle, useLayoutEffect } from 'react';
import { useValidateFormRequiredProps, dsI18n, Languages, getCommonClassNameDefault, getCommonAutoCompleteDefault, getCommonFormVariantDefault } from '@skatteetaten/ds-core-utils';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import { LabelWithHelp } from '../LabelWithHelp/index.esm.js';
import '@skatteetaten/ds-icons';
import '../Help/index.esm.js';
import 'react-i18next';
import '@skatteetaten/ds-buttons';
import './styles.css';


const getTextFieldAsDefault = ()=>'input';

var styles = {
    "textbox": "TextField-module_textbox__LgV5e",
    "textbox_large": "TextField-module_textbox_large__9IOt0",
    "textbox_multiline": "TextField-module_textbox_multiline__R3IL6",
    "textbox_autosize": "TextField-module_textbox_autosize__HTv5M"
};

const TextField = /*#__PURE__*/ forwardRef(({ id: externalId, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, as: Tag = getTextFieldAsDefault(), autosize, description, errorMessage, helpSvgPath, helpText, label, thousandSeparator, titleHelpSvg, variant = getCommonFormVariantDefault(), autoComplete = getCommonAutoCompleteDefault(), defaultValue, disabled, form, inputMode, maxLength, minLength, list, name, pattern, placeholder, readOnly, required, rows, value, hideLabel, showRequiredMark, onBlur, onChange, onFocus, onHelpToggle }, ref)=>{
    useValidateFormRequiredProps({
        required,
        showRequiredMark
    });
    const errorId = `textFieldErrorId-${useId()}`;
    const generatedId = `textFieldTextboxId-${useId()}`;
    const textboxId = externalId ?? generatedId;
    const textboxRef = useRef(null);
    useImperativeHandle(ref, ()=>({
            textboxRef: textboxRef
        }));
    useLayoutEffect(()=>{
        if (autosize) {
            resizeTextArea();
        }
    }, [
        autosize,
        value
    ]);
    const resizeTextArea = ()=>{
        const textArea = textboxRef.current;
        textArea.style.height = 'inherit';
        const { scrollHeight } = textArea;
        const includeBorderAndMore = textArea.offsetHeight - textArea.clientHeight;
        textArea.style.height = `${scrollHeight + includeBorderAndMore}px`;
    };
    const separator = dsI18n.language === Languages.Engelsk ? ',' : ' ';
    const addSpacesOrCommas = (value)=>value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    const removeNonNumeric = (value)=>{
        const trimmed = value.trim();
        const isNegative = /^-/.test(trimmed);
        const numberOnly = trimmed.replaceAll(/\D+/g, '');
        return isNegative ? `-${numberOnly}` : numberOnly;
    };
    const handleKeyDown = (e)=>{
        if (!thousandSeparator) return;
        const input = e.currentTarget;
        const cursorPosition = input.selectionStart || 0;
        const value = input.value;
        const isPreviousCharacterSeparator = /[, ]/.test(value[cursorPosition - 1]);
        const selectionLength = (input.selectionEnd || 0) - (input.selectionStart || 0);
        const modifierKeys = [
            'Alt',
            'AltGraph',
            'Control',
            'Meta',
            'Shift'
        ];
        const isAnyModifierKeyPressed = modifierKeys.some((key)=>e.getModifierState(key));
        if (e.key === 'Backspace' && !isAnyModifierKeyPressed && cursorPosition > 0 && isPreviousCharacterSeparator && selectionLength === 0) {
            e.preventDefault();
            const deletePosition = cursorPosition - 1;
            const newValue = value.slice(0, deletePosition - 1) + value.slice(cursorPosition);
            const formattedValue = addSpacesOrCommas(removeNonNumeric(newValue));
            const separatorsInOldValue = value.match(/[, ]/g)?.length || 0;
            const separatorsInNewValue = formattedValue.match(/[, ]/g)?.length || 0;
            // hvis det ble fjernet en separator, må vi flytte markøren et ekstra hopp til venstre, med mindre vi sletter den første separatoren.
            const separatorWasRemoved = separatorsInOldValue > separatorsInNewValue && cursorPosition > 2;
            input.value = formattedValue;
            const newPosition = deletePosition - 1 - (separatorWasRemoved ? 1 : 0);
            requestAnimationFrame(()=>{
                input.setSelectionRange(newPosition, newPosition);
            });
        }
    };
    const handleChange = (e)=>{
        if (thousandSeparator) {
            const input = e.target;
            const cursorPosition = input.selectionStart || 0;
            const oldValue = input.value;
            const digitsBeforeCursor = oldValue.substring(0, cursorPosition).replace(/\D/g, '').length;
            const formattedValue = addSpacesOrCommas(removeNonNumeric(input.value));
            input.value = formattedValue;
            let newPosition = 0;
            let digitCount = 0;
            for(let i = 0; i < formattedValue.length; i++){
                if (/\d/.test(formattedValue[i])) {
                    digitCount++;
                    if (digitCount > digitsBeforeCursor) {
                        break;
                    }
                }
                newPosition = i + 1;
            }
            input.setSelectionRange(newPosition, newPosition);
        }
        if (autosize && defaultValue !== undefined) {
            resizeTextArea();
        }
        onChange?.(e);
    };
    /* Slik at value har riktig format også før bruker begynner å skrive i feltet */ if (thousandSeparator && value) {
        value = addSpacesOrCommas(removeNonNumeric(value.toString()));
    }
    if (thousandSeparator && defaultValue) {
        defaultValue = addSpacesOrCommas(removeNonNumeric(defaultValue.toString()));
    }
    const isLarge = variant === 'large';
    const multilineTextboxClassName = Tag === 'textarea' ? styles.textbox_multiline : '';
    const autosizeTextarea = autosize ? styles.textbox_autosize : '';
    const textboxClassName = `${styles.textbox} ${isLarge ? styles.textbox_large : ''} ${multilineTextboxClassName} ${autosizeTextarea} ${classNames?.textbox ?? ''}`.trim();
    return /*#__PURE__*/ jsxs("div", {
        className: `${className} ${classNames?.container ?? ''}`.trim(),
        lang: lang,
        children: [
            /*#__PURE__*/ jsx(LabelWithHelp, {
                classNames: classNames,
                htmlFor: textboxId,
                hideLabel: hideLabel,
                showRequiredMark: showRequiredMark,
                description: description,
                helpSvgPath: helpSvgPath,
                helpText: helpText,
                titleHelpSvg: titleHelpSvg,
                onHelpToggle: onHelpToggle,
                children: label
            }),
            /*#__PURE__*/ jsx(Tag, {
                ref: textboxRef,
                id: textboxId,
                className: textboxClassName,
                "data-testid": dataTestId,
                autoComplete: autoComplete,
                defaultValue: defaultValue,
                disabled: disabled,
                form: form,
                inputMode: inputMode,
                list: list,
                maxLength: maxLength,
                minLength: minLength,
                name: name,
                pattern: pattern,
                placeholder: placeholder,
                readOnly: readOnly,
                required: required,
                rows: rows,
                value: value,
                "aria-describedby": errorMessage ? errorId : undefined,
                "aria-invalid": !!errorMessage || undefined,
                onBlur: onBlur,
                onChange: handleChange,
                onFocus: onFocus,
                onKeyDown: handleKeyDown
            }),
            /*#__PURE__*/ jsx(ErrorMessage, {
                id: errorId,
                showError: !!errorMessage,
                className: classNames?.errorMessage,
                children: errorMessage
            })
        ]
    });
});
TextField.displayName = 'TextField';

export { TextField, getTextFieldAsDefault };
